import React, { useEffect } from "react";
import confetti from "canvas-confetti";

// components
import classNames from "classnames";

// styles
import { Button, ButtonGroup } from "@clockwise/design-system";
import { makeStyles } from "@material-ui/styles";
import { greys, colors, gradient } from "@styles/color.styles";
import { Reply, Link } from "@material-ui/icons";
import { fontFamilyHelvetica, fontFamilyIdealSans } from "@styles/type.styles";
import { animated, useSpring, config, Globals } from "@react-spring/web";

// util
import { getRandomIntInRange } from "@util/browser.util";
import { formatDuration } from "@util/duration.util";
import { AVAILABILITY_TEXT_SHORT } from "@ui/scheduling-links/constants";
import { AvailabilityType } from "@clockwise/schema";
import { useBoop } from "@util/animations.util";

const AnimatedButton = animated(Button);
export interface SchedulingLinkCardProps {
  link: {
    linkUrl: string;
    linkName: string;
    meetingSettings: {
      id?: string | null | undefined;
      name: string;
      slug: string;
      availabilityType: AvailabilityType;
      timingSettings: {
        durationMinutes: number;
      };
    };
  };
  shakeShare: boolean;
  onCopyLink: (linkUrl: string, meetingSettingsId: string | null) => Promise<boolean>;
  onCopyTimes: () => void;
  onOpen: (linkUrl: string, meetingSettingsId: string | null) => Promise<void>;
  className?: string;
  copyTimesLinkEnabled: boolean;
}

const useStyles = makeStyles({
  container: {
    border: `1px solid ${greys.cloudyBlue}`,
    borderRadius: 8,
    padding: "10px 14px 4px",
    position: "relative", // Needed to position the copied container absolutely.
    cursor: "pointer",
    maxWidth: 425,
  },
  linkName: {
    color: greys.slate,
    fontSize: ".875rem",
    marginBottom: "4px",
  },
  headingL: {
    fontFamily: fontFamilyIdealSans,
    fontWeight: "bold",
    fontSize: `${14 / 16}rem`,
    lineHeight: 1.2,
    color: colors.blueDusk.darker,
    margin: "4px 0px 9px 0px",
  },
  button: {
    fontSize: `${12 / 16}rem`,
    fontFamily: fontFamilyHelvetica,
    color: colors.green.darker,
  },
  copy: {
    marginLeft: -8,
  },
  open: {
    marginLeft: 5,
  },
  eventDetailsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 9,
    fontFamily: fontFamilyHelvetica,
    fontSize: `${13 / 16}rem`,
    lineHeight: 1,
    color: greys.slate,

    // icons
    "& > svg": {
      fontSize: 18,
      fill: greys.slate,
    },

    // override browser margins for p tags
    "& > p": {
      margin: 0,
    },
  },
  copied: {
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: gradient,
    borderRadius: 7,
  },
  copiedText: {
    fontFamily: fontFamilyHelvetica,
    fontSize: `${13.75 / 16}rem`,
    color: "white",
    fontWeight: "bold",
  },
  confettiCanvas: {
    position: "absolute",
    height: "100%",
    width: "100%",
  },
});

export const SchedulingLinkCard = ({
  link: {
    linkUrl,
    meetingSettings: {
      id = null,
      name,
      availabilityType,
      timingSettings: { durationMinutes: duration },
    },
  },
  shakeShare,
  onCopyLink,
  onCopyTimes,
  onOpen,
  className,
  copyTimesLinkEnabled,
}: SchedulingLinkCardProps) => {
  const classes = useStyles();

  // refs
  const confettiCanvasRef = React.useRef<HTMLCanvasElement>(null);
  const confettiRef = React.useRef<confetti.CreateTypes | null>(null);

  const [copied, setCopied] = React.useState(false);
  const formattedDuration = formatDuration(duration);

  const { contentOpacity, copiedMsgOpacity } = useSpring({
    contentOpacity: copied ? 0 : 1,
    copiedMsgOpacity: copied ? 1 : 0,
    config: { mass: 1, tension: 200, friction: 23 },
  });

  const { textScale } = useSpring({
    textScale: copied ? 0 : 1,
    immediate: !copied, // Prevents a second animation when copied is returned to false.
    config: { mass: 1, tension: 170, friction: 18, precision: 0.02 },
  });

  const scaleInterpolateRange = [0, 0.25, 0.35, 0.45, 0.5, 0.55, 0.65, 0.75, 1];
  const scaleInterpolateOutput = [1.04, 1.06, 1.07, 1.08, 1.1, 1.09, 1.08, 1.06, 1];

  const [boopStyle, triggerBoop] = useBoop({
    scale: 1.11,
    rotation: 3,
    timing: 170,
    springConfig: {
      tension: 355,
      friction: 8,
    },
  });
  useEffect(() => {
    // Since this is custom timed animation, we need to manually check the react-spring global skipAnimation setting to avoid unexpected jitter in UI tests
    if (shakeShare && !Globals.skipAnimation) {
      setHovering(true);
      setTimeout(() => triggerBoop(), 350);
      setTimeout(() => setHovering(false), 2000);
    }
  }, [shakeShare, triggerBoop]);

  React.useEffect(() => {
    if (copied && confettiCanvasRef.current) {
      if (!confettiRef.current) {
        confettiRef.current = confetti.create(confettiCanvasRef.current, {
          useWorker: false, // Cannot use web workers with multiple canvases.
          // Confetti would only pop on a single scheduling-link-card otherwise.
          resize: true,
        });
      }

      const randomColors = ["#FFFFFF", "#FFDE34"]; // #FFDE34 is emoji yellow
      randomColors.splice(getRandomIntInRange(0, 1), getRandomIntInRange(0, 1));

      // Put into an array, so we can add lots of random confettis
      const confettis = [
        {
          // Left to right, orginating from near the copy button
          particleCount: getRandomIntInRange(80, 120),
          spread: getRandomIntInRange(50, 150),
          startVelocity: getRandomIntInRange(18, 26),
          ticks: getRandomIntInRange(280, 320),
          drift: getRandomIntInRange(1, 6) / 10,
          origin: { x: 0.2, y: 1.2 }, // Sets it to pop from the copy button.
          angle: getRandomIntInRange(52, 70),
          colors: randomColors,
          scalar: 0.2,
          disableForReducedMotion: true,
        },
      ];

      void confettiRef.current(confettis[Math.floor(Math.random() * confettis.length)]);
    }
  }, [copied]);

  const [hovering, setHovering] = React.useState(false);
  const [{ scale }] = useSpring(
    {
      scale: hovering ? 1.035 : 1.0,
      config: { ...config.stiff },
    },
    [hovering],
  );

  const handleCopyLink = React.useCallback(async () => {
    const copied = await onCopyLink(linkUrl, id);
    if (copied) {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        if (confettiRef.current) {
          confettiRef.current.reset?.();
        }
      }, 2100);
    }
  }, [onCopyLink, linkUrl, setCopied, confettiRef, id]);

  return (
    <animated.div
      style={{
        transform: textScale
          .to({
            range: scaleInterpolateRange,
            output: scaleInterpolateOutput.map((s) => s - (s - 1) * 0.7), // Scale, but not as much.
          })
          .to((x) => `scale(${x})`),
        backgroundColor: copied ? gradient : "transparent",
        scale,
      }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onClick={() => onOpen(linkUrl, id)}
      cw-id="links-sidebar-card"
      className={classNames([classes.container, className, "cw-shadow-md"])}
      title="Open Link in new tab"
    >
      <animated.div className="cw-flex cw-flex-col cw-gap-1" style={{ opacity: contentOpacity }}>
        <div className="cw-flex cw-flex-col cw-gap-1 cw-pt-1">
          <h2
            className="cw-font-brand cw-font-bold cw-text-sm cw-leading-tight cw-text-dusk-standard cw-m-0"
            cw-id="links-card-meeting-title"
          >
            {name}
          </h2>
          <div className="cw-font-body cw-text-sm cw-text-slate-standard">
            {formattedDuration} • {AVAILABILITY_TEXT_SHORT[availabilityType]}
          </div>
        </div>
        <div className="-cw-ml-[10px]">
          <ButtonGroup>
            {copyTimesLinkEnabled && (
              <AnimatedButton
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onCopyTimes();
                }}
                variant="text"
                size="small"
                startIcon={Reply}
                sentiment="positive"
                cw-id="links-card-copy-times-button"
                style={boopStyle}
              >
                Share
              </AnimatedButton>
            )}
            <Button
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                e.preventDefault();
                void handleCopyLink();
              }}
              variant="text"
              size="small"
              sentiment={copyTimesLinkEnabled ? "neutral" : "positive"}
              startIcon={Link}
              cw-id="links-card-copy-link-button"
            >
              Copy link
            </Button>
          </ButtonGroup>
        </div>
      </animated.div>
      <animated.div
        className={classes.copied}
        style={{ opacity: copiedMsgOpacity, visibility: copied ? "visible" : "hidden" }}
      >
        <canvas ref={confettiCanvasRef} className={classes.confettiCanvas} />
        <animated.div
          className={classes.copiedText}
          style={{
            transform: textScale
              .to({
                range: scaleInterpolateRange,
                output: scaleInterpolateOutput,
              })
              .to((x) => `scale(${x})`),
          }}
        >
          Link copied to your clipboard 🙌
        </animated.div>
      </animated.div>
    </animated.div>
  );
};
